import React, { memo, useEffect, useMemo, useState } from "react";
import { css, useTheme } from "@emotion/react";

type Props = {
    className?: string,
    percent?: number,
    delay?: number,
    baseColour?: string,
    completeColour?: string,
    barSize?: string,
};

const ProgressBar: React.FC<Props> = ({
    className,
    percent = 0,
    delay = 200,
    baseColour = null,
    completeColour = null,
    barSize = 'sm'
}) => {
    const theme = useTheme();
    const [animate, setAnimate] = useState(false);

    barSize = barSize === 'lg' ? '16px' : '12px';

    const backgroundColour = useMemo(() => {
        switch(true) {
            case percent < 25:
                return completeColour || theme.colours.crimson;
            case percent < 50:
                return completeColour || theme.colours.sun;
            case percent < 75:
                return completeColour || theme.colours.sushi;
            default:
                return completeColour || theme.colours.eucalyptus;
        }
    }, [
        completeColour,
        percent,
        theme.colours.crimson,
        theme.colours.eucalyptus,
        theme.colours.sun,
        theme.colours.sushi
    ]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setAnimate(true);
        }, delay);

        return () => {
            clearTimeout(timeout);
        }
    }, [delay]);

    const style = (
        percent: number,
        backgroundColour: string,
        barSize: string,
        baseColour: string | null
    ) => css`
        width: 100%;
        height: ${barSize};
        border-radius: 8px;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        background-color: ${baseColour || theme.colours.alto3};
        position: relative;
        overflow: hidden;

        &:after {
            transition: color ${theme.transitions.transitionSlower}, width ${theme.transitions.transitionSlower};
            position: absolute;
            border-radius: 8px;
            height: 100%;
            left: 0;
            width: ${percent}%;
            content: '';
            background-color: ${backgroundColour};
        }
    `;

    return (
        <div
            css={style(animate ? percent : 0, backgroundColour, barSize, baseColour)}
            className={className}
            data-print-class-name={className}
            data-percent={`${percent}%`}
            data-background-colour={backgroundColour}
        />
    );
}

export default memo(ProgressBar);
